import { inject, Injectable } from '@angular/core';
import { AppDataConfig } from '@core/configs/app-data.config';
import {
  AppDataContainers,
  AppDataFieldsRequest,
  ContainerPaging,
} from '@core/models/interfaces/app-data/app-data';
import { AppDataField } from '@core/models/interfaces/app-data/app-data-field';
import { AppDataResponse } from '@core/models/interfaces/http/app-data-response';
import { AppDataProviderService } from '@core/services/data-processing/app-data-provider.service';
import { StorageService } from '@core/services/storages/storage.service';
import { AUTH_TOKEN } from '@core/tokens/tokens';
import { hasMorePages } from '@core/utils/app-data.utils';
import {
  catchError,
  EMPTY,
  expand,
  forkJoin,
  last,
  map,
  Observable,
  of,
  retry,
  switchMap,
  takeWhile,
  throwError,
  timer,
} from 'rxjs';

export interface AppDataLoadResult {
  /** The containers as they now stand in storage. */
  state: AppDataContainers;
  /** Batched containers the response left half-delivered, keyed by container. */
  unfinished: Record<string, ContainerPaging>;
}

@Injectable({
  providedIn: 'root',
})
export class AppDataFetchingService {
  private readonly AUTH_TOKEN = inject(AUTH_TOKEN);

  private appDataProvider = inject(AppDataProviderService);
  private appStorage = inject(StorageService);

  private authToken$ = this.appStorage.getField(this.AUTH_TOKEN);

  /**
   * Fetches and stores the main application data, followed by loading of specific container data.
   * @returns Observable indicating the completion status.
   */
  public loadAppData(payload: {
    fields?: AppDataFieldsRequest | null;
    reset?: boolean;
  }): Observable<AppDataLoadResult> {
    const { reset, fields } = payload;

    const requestArgs: [AppDataFieldsRequest?, boolean?] = [];
    if (fields) requestArgs[0] = fields;
    if (typeof reset === 'boolean') requestArgs[1] = reset;

    // R38. A `catchError(() => of({}))` used to sit here, which turned every
    // failure into an ordinary empty result: the effect dispatched
    // `loadAppDataSuccess`, the loading flag went out, and the user was shown
    // stale data with no hint that the refresh had failed. The error now reaches
    // the effect, which is where the decision between success and failure lives.
    return this.appDataProvider.getAppData(...requestArgs).pipe(
      switchMap((response) => {
        // A response can arrive without `data`. A success carrying nothing is a
        // no-op — a routine refresh with nothing changed — and treating it as an
        // error would end that refresh in a failed load. An `error` envelope
        // never gets this far (the error interceptor raises it), but any other
        // non-success answer without containers is still a failure and must say
        // so here, where the effect decides between success and failure.
        if (!response.data && response.status !== 'success') {
          return throwError(
            () =>
              new Error(
                `App data request answered "${response.status}" with no data`
              )
          );
        }

        const containers = response.data ?? {};

        return this.appStorage.upsertFields(containers, !!reset).pipe(
          switchMap(() =>
            fields && Object.keys(fields).length
              ? this.appStorage.getMultipleFields(Object.keys(fields))
              : this.appStorage.getAllFields()
          ),
          map((state) => ({
            state,
            unfinished: this.unfinishedContainers(containers),
          }))
        );
      })
    );
  }

  /**
   * Works out which batched containers the response left half-delivered, and on
   * what terms to ask for the rest.
   *
   * R121. Continuation has to be decided from the response, the way the legacy
   * service did it. Deciding it from storage instead — "is the stored collection
   * shorter than the stored total?" — cannot see a delta at all: an anchored
   * request answers with only the rows that changed, so a complete-looking cache
   * would end the load early and a large delta would be truncated at one page.
   */
  private unfinishedContainers(
    data: AppDataContainers
  ): Record<string, ContainerPaging> {
    const unfinished: Record<string, ContainerPaging> = {};

    AppDataConfig.batchedContainers.forEach((key) => {
      const metaData = (data?.[key] as AppDataField)?.metaData;
      if (!metaData) return;

      if (hasMorePages(metaData)) {
        const limit = Number(metaData['limit']) || 0;
        const offset = Number(metaData['offset']) || 0;
        unfinished[key] = {
          lastUpdatedDate: Number(metaData['lastUpdatedDate']) || 0,
          limit,
          offset: limit + offset,
        };
      }
    });

    return unfinished;
  }
  /**
   * Loads data for multiple containers concurrently.
   * @returns Observable completing when all loading operations finish.
   */
  public loadBatchedContainers(fields: {
    [K in keyof AppDataContainers]?: AppDataContainers[K] | null;
  }): Observable<AppDataContainers> {
    const loadOperations: Observable<Record<string, AppDataField | null>>[] =
      [];
    Object.keys(fields ?? {}).forEach((key) => {
      if (!AppDataConfig.batchedContainers.includes(key)) return;

      // Two callers, two kinds of value. A response-driven continuation carries
      // explicit paging and is followed exactly; hydration from IndexedDB passes
      // the stored container, and there the storage check is the right one — it
      // is how an interrupted load resumes on the next start.
      const paging = fields[key] as Partial<ContainerPaging>;
      loadOperations.push(
        typeof paging?.offset === 'number' && typeof paging?.limit === 'number'
          ? this.loadContainerRecursively(key, {
              lastUpdatedDate: paging.lastUpdatedDate ?? 0,
              limit: paging.limit,
              offset: paging.offset,
            })
          : this.loadContainerIfNeeded(key)
      );
    });

    if (!loadOperations.length) {
      return of({});
    }

    return forkJoin(loadOperations).pipe(
      map((containers) => {
        const batchedContainers: Record<string, AppDataField | null> = {};
        containers?.forEach((container) => {
          Object.entries(container).forEach(([key, value]) => {
            batchedContainers[key] = value;
          });
        });
        return batchedContainers;
      })
    );
  }

  /**
   * Checks and conditionally loads data for a specific container if needed.
   * @param containerName Name of the container to check and load.
   * @returns Observable completing when operation finishes.
   */
  public loadContainerIfNeeded(
    containerName: string
  ): Observable<Record<string, AppDataField | null>> {
    return this.appStorage.getField<AppDataField>(containerName).pipe(
      switchMap((data: AppDataField | null) => {
        // R44. `data.metaData` and `data.collection` used to be read without a
        // guard right after `data?.` above, so a container that is not in
        // IndexedDB yet — the very case this method exists for — threw.
        const loaded = data?.collection?.length ?? 0;
        const total = data?.metaData?.['transactionsCount'];

        // Only a known total can prove the container is complete. `loaded >=
        // undefined` is false, so an unknown total keeps meaning "fetch", but
        // now it says so rather than relying on a comparison with undefined.
        if (typeof total === 'number' && loaded >= total) {
          return of({});
        }

        return this.loadContainerRecursively(containerName, {
          // R121. Was a hard-coded zero, which asked for the list from the top
          // even when the client knew exactly how fresh its copy was.
          lastUpdatedDate: Number(data?.metaData?.['lastUpdatedDate']) || 0,
          limit:
            data?.metaData?.['limit'] ||
            AppDataConfig.containers[containerName]?.limit ||
            0,
          offset: loaded,
        });
      })
    );
  }

  /**
   * Recursively fetches and stores paginated container data until fully loaded.
   * @param containerName Container name to load recursively.
   * @param params Pagination and update parameters.
   * @returns Observable completing when all paginated data is loaded and stored.
   */
  private loadContainerRecursively(
    containerName: string,
    params: { lastUpdatedDate: number; limit: number; offset: number }
  ): Observable<Record<string, AppDataField | null>> {
    return this.fetchAndStoreData(containerName, params).pipe(
      expand((response: AppDataField) => {
        // a page without metaData used to throw on the destructure; a paged
        // container always carries it — stopping is the honest fallback (A1.4)
        if (!response.metaData) return EMPTY;

        // Paged containers always carry numeric paging (guarded above);
        // AppDataField itself stays a loose record
        const { transactionsCount, offset, limit } = response.metaData as {
          transactionsCount: number;
          offset: number;
          limit: number;
        };
        const nextOffset = offset + limit;

        if (nextOffset >= transactionsCount) return EMPTY;

        return this.fetchAndStoreData(containerName, {
          ...params,
          offset: nextOffset,
        });
      }),
      last(),
      // R117. One processing pass over the finished collection, not one per
      // page. The pages themselves are appended by `upsertItemsIntoFieldCollection`
      // and their paging counters written by `updateFieldMetadata`; neither
      // recomputes the derived metadata (`periods`, `hashtags`,
      // `firstHistoryDate`, `lastUpdatedDate`), so it is done here, once, over
      // everything that landed — the shape the legacy service had.
      switchMap(() => this.appStorage.getField<AppDataField>(containerName)),
      switchMap((field: AppDataField | null) =>
        this.appStorage
          .upsertFields({ [containerName]: field })
          .pipe(
            switchMap(() =>
              this.appStorage.getField<AppDataField>(containerName)
            )
          )
      ),
      switchMap((field: AppDataField | null) => {
        return of({
          [containerName]: field,
        });
      }),
      catchError((error) => this.fallBackToStored(containerName, error))
    );
  }

  /**
   * What a container load answers when it could not finish: whatever storage already holds.
   *
   * R118. This used to be `EMPTY`, and three different failures arrived here — a page that
   * failed all its retries, a session that ended mid-load (`takeWhile` on the token completes
   * the chain without emitting), and the `EmptyError` that `last()` then throws. `forkJoin` in
   * `loadBatchedContainers` does not emit at all when one of its sources completes empty, so the
   * whole batched load completed silently with no value: no success action, no failure action,
   * nothing for the loading counter to come down on. The indicator stayed up and the data never
   * arrived, with only a console line to say why.
   *
   * Emitting the cached container keeps the pipeline honest — the caller gets what exists, the
   * batch completes, and the warning says the copy may be short.
   */
  private fallBackToStored(
    containerName: string,
    error: unknown
  ): Observable<Record<string, AppDataField | null>> {
    console.warn(
      `Could not finish loading "${containerName}" — falling back to the cached copy, which may be incomplete:`,
      error
    );

    return this.appStorage
      .getField<AppDataField>(containerName)
      .pipe(map((field) => (field ? { [containerName]: field } : {})));
  }

  /**
   * Fetches data for a container and stores it along with metadata updates.
   * Implements retry logic.
   * @param containerName Name of the container.
   * @param loadData Loading parameters including pagination.
   * @returns Observable containing the loaded AppDataField.
   */
  private fetchAndStoreData(
    containerName: string,
    loadData: { lastUpdatedDate: number; limit: number; offset: number }
  ): Observable<AppDataField> {
    return this.authToken$.pipe(
      takeWhile((token) => !!token),
      switchMap(() =>
        this.appDataProvider.getAppData({ [containerName]: loadData }).pipe(
          retry({ count: 3, delay: (attempt) => timer(attempt * 1000) }),
          switchMap((response: AppDataResponse) => {
            const batch = (response.data?.[containerName] ??
              {}) as AppDataField;

            // R121. `lastUpdatedDate` is deliberately not written here. The
            // anchor is derived from the newest row by `processAppData` when the
            // first page lands, and the later pages carry older rows, so writing
            // the request's value back — a zero — is what kept the anchor pinned
            // at 0 for every account large enough to paginate.
            const meta = batch.metaData;
            const updateMetadata$ = meta
              ? this.appStorage.updateFieldMetadata(containerName, {
                  limit: meta.limit,
                  offset: meta.offset,
                })
              : of(void 0);

            return forkJoin([
              this.appStorage.upsertItemsIntoFieldCollection(
                containerName,
                batch.collection ?? []
              ),
              updateMetadata$,
            ]).pipe(map(() => batch));
          }),
          catchError((error) => {
            // R118. This returned `of()` — no emission, just completion — which the recursive
            // loader could not tell apart from "there was nothing more to fetch". Reporting it
            // lets the one handler above decide, and it decides in favour of the cached copy.
            console.error(
              `❌ Failed to fetch data for ${containerName} after retries: ${error}`
            );
            return throwError(() => error);
          })
        )
      )
    );
  }
}
